import { Component, OnDestroy, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WorkflowService } from '../../../shared/services/workflow/workflow.service';
import { PermissionsService } from '../../../shared/services/permissions/permissions.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { WorkflowCreate } from '../../../shared/interfaces/project.interface';
import { StepType } from '../../../shared/interfaces/step-type.interface';
import { Workflow } from '@app/shared/interfaces';
import { MessageService } from '../../../shared/services/message/message.service';
import { RELOAD_NOTEBOOKS } from '../../../shared/constants/general.constants';
import { LocalstorageHelper } from '../../../shared/helpers/localstorage.helper';

export interface WorkflowCreateDialogData {
  projectId: string;
  existingWorkflows?: Workflow[];
}

const WORKFLOW_CREATE_CONFIG = {
  DEFAULT_CHUNK_NAME: 'Welcome',
} as const;

/**
 * Custom validator to check for unique workflow names
 * Handles case-insensitive comparison and trims whitespace
 */
function uniqueWorkflowNameValidator(
  existingWorkflows: Workflow[] = [],
): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Let the required validator handle empty values
    }

    const trimmedValue = control.value.trim().toLowerCase();

    // Check if the trimmed, lowercase name already exists
    const isDuplicate = existingWorkflows.some(
      (workflow) => workflow.name.trim().toLowerCase() === trimmedValue,
    );

    return isDuplicate
      ? { uniqueWorkflowName: { value: control.value } }
      : null;
  };
}

@Component({
  selector: 'app-workflow-create-dialog',
  templateUrl: './workflow-create-dialog.component.html',
  styleUrls: ['./workflow-create-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
})
export class WorkflowCreateDialogComponent implements OnDestroy, OnInit {
  private readonly fb = inject(FormBuilder);
  private notebookService = inject(WorkflowService);
  private permissionsService = inject(PermissionsService);
  private toastr = inject(ToastrService);
  private messageService = inject(MessageService);
  dialogRef = inject<MatDialogRef<WorkflowCreateDialogComponent>>(MatDialogRef);
  data = inject<WorkflowCreateDialogData>(MAT_DIALOG_DATA);

  workflowForm: FormGroup;
  isCreating = signal(false);
  isLoadingStepTypes = signal(true);
  isLoadingChunkTypes = signal(true); // Legacy compatibility
  stepTypes = signal<StepType[]>([]);
  chunkTypes = signal<StepType[]>([]); // Legacy compatibility
  private subscription = new Subscription();

  readonly WORKFLOW_CREATE_CONFIG = WORKFLOW_CREATE_CONFIG;

  constructor() {
    const data = this.data;

    this.workflowForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(100),
          uniqueWorkflowNameValidator(data.existingWorkflows || []),
        ],
      ],
      chunkName: [
        WORKFLOW_CREATE_CONFIG.DEFAULT_CHUNK_NAME,
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(100),
        ],
      ],
      stepTypeId: ['', [Validators.required]],
      chunkTypeId: ['', [Validators.required]], // Legacy compatibility
    });

    this.useStepTypesMode();
  }

  ngOnInit(): void {
    this.loadStepTypes();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadStepTypes(): void {
    this.isLoadingStepTypes.set(true);
    this.isLoadingChunkTypes.set(true); // For template compatibility

    const stepTypes$ = this.permissionsService.getStepTypes?.();
    if (!stepTypes$) {
      this.isLoadingStepTypes.set(false);
      this.useChunkTypesMode();
      this.loadChunkTypes();
      return;
    }

    const stepTypesSub = stepTypes$.subscribe({
      next: (stepTypes = []) => {
        this.stepTypes.set(stepTypes);
        this.isLoadingStepTypes.set(false);

        if (!stepTypes.length) {
          this.useChunkTypesMode();
          this.loadChunkTypes();
          return;
        }

        this.useStepTypesMode();
        this.isLoadingChunkTypes.set(false);

        const defaultTypeId = stepTypes[0].steptypeId;
        this.workflowForm.patchValue(
          {
            stepTypeId: defaultTypeId,
            chunkTypeId: defaultTypeId, // Legacy compatibility
          },
          { emitEvent: false },
        );
      },
      error: () => {
        this.toastr.error('Failed to load step types. Please try again.');
        this.isLoadingStepTypes.set(false);
        this.useChunkTypesMode();
        this.loadChunkTypes();
      },
    });

    this.subscription.add(stepTypesSub);
  }

  private loadChunkTypes(): void {
    this.isLoadingChunkTypes.set(true);
    this.useChunkTypesMode();

    const chunkTypes$ = this.permissionsService.getChunkTypes?.();
    if (!chunkTypes$) {
      this.isLoadingChunkTypes.set(false);
      this.toastr.error('Failed to load step types. Please try again.');
      return;
    }

    const chunkTypesSub = chunkTypes$.subscribe({
      next: (chunkTypes = []) => {
        this.chunkTypes.set(chunkTypes);
        this.isLoadingChunkTypes.set(false);

        if (!chunkTypes.length) {
          this.toastr.error('Failed to load step types. Please try again.');
          return;
        }

        const defaultTypeId = chunkTypes[0].steptypeId;
        this.workflowForm.patchValue(
          {
            chunkTypeId: defaultTypeId,
          },
          { emitEvent: false },
        );

        // Keep legacy compatibility by mirroring the value for disabled controls
        this.workflowForm.patchValue(
          {
            stepTypeId: defaultTypeId,
          },
          { emitEvent: false },
        );
      },
      error: () => {
        this.toastr.error('Failed to load step types. Please try again.');
        this.isLoadingChunkTypes.set(false);
      },
    });

    this.subscription.add(chunkTypesSub);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.workflowForm.valid && !this.isCreating()) {
      this.createWorkflow();
    }
  }

  private createWorkflow(): void {
    this.isCreating.set(true);
    this.workflowForm.disable();

    const formValue = this.workflowForm.getRawValue();
    const stepTypeId = formValue.stepTypeId || formValue.chunkTypeId;
    const workflowData: WorkflowCreate = {
      name: formValue.name.trim(),
      stepName: formValue.chunkName.trim(),
      stepTypeId: stepTypeId,
      // Legacy field names for backward compatibility
      chunkName: formValue.chunkName.trim(),
      chunkTypeId: stepTypeId,
    };

    const createSub = this.notebookService
      .create(workflowData, this.data.projectId)
      .subscribe({
        next: async (newNotebook) => {
          this.toastr.success('Workflow created successfully!');

          // SCRUM-579: Remember the step type the user picked so subsequent
          // "Add Step" dialogs in this workflow default to it instead of the
          // previous step's type. Best-effort — failures here must not block
          // workflow creation.
          const newWorkflowId = newNotebook?.workflow?.workflowId;
          if (newWorkflowId && stepTypeId) {
            try {
              LocalstorageHelper.setWorkflowDefaultStepType(
                newWorkflowId,
                stepTypeId,
              );
            } catch {
              /* localStorage may be unavailable (quota, privacy mode) */
            }
          }

          // Send message to reload workflows
          this.messageService.sendMessage(RELOAD_NOTEBOOKS);
          this.dialogRef.close(newNotebook);
        },
        error: () => {
          this.toastr.error('Failed to create workflow. Please try again.');
          this.isCreating.set(false);
          this.workflowForm.enable();
          if (this.stepTypes().length > 0) {
            this.chunkTypeControl?.disable({ emitEvent: false });
          } else {
            this.stepTypeControl?.disable({ emitEvent: false });
          }
        },
      });

    this.subscription.add(createSub);
  }

  // Form validation helpers
  get nameControl() {
    return this.workflowForm.get('name');
  }

  get chunkNameControl() {
    return this.workflowForm.get('chunkName');
  }

  get stepTypeControl() {
    return this.workflowForm.get('stepTypeId');
  }

  get chunkTypeControl() {
    return this.workflowForm.get('chunkTypeId');
  }

  private useStepTypesMode(): void {
    this.stepTypeControl?.enable({ emitEvent: false });
    this.chunkTypeControl?.disable({ emitEvent: false });
  }

  private useChunkTypesMode(): void {
    this.chunkTypeControl?.enable({ emitEvent: false });
    this.stepTypeControl?.disable({ emitEvent: false });
  }

  get isNameInvalid(): boolean {
    const control = this.nameControl;
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  get nameErrorMessage(): string {
    const control = this.nameControl;
    if (control?.errors?.['required']) {
      return 'Workflow name is required';
    }
    if (control?.errors?.['minlength']) {
      return 'Workflow name must be at least 1 character';
    }
    if (control?.errors?.['maxlength']) {
      return 'Workflow name cannot exceed 100 characters';
    }
    if (control?.errors?.['uniqueWorkflowName']) {
      return 'A workflow with this name already exists';
    }
    return '';
  }

  get isChunkNameInvalid(): boolean {
    const control = this.chunkNameControl;
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  get chunkNameErrorMessage(): string {
    const control = this.chunkNameControl;
    if (control?.errors?.['required']) {
      return 'Step name is required';
    }
    if (control?.errors?.['minlength']) {
      return 'Step name must be at least 1 character';
    }
    if (control?.errors?.['maxlength']) {
      return 'Step name cannot exceed 100 characters';
    }
    return '';
  }

  get isStepTypeInvalid(): boolean {
    const control = this.stepTypeControl || this.chunkTypeControl;
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  get stepTypeErrorMessage(): string {
    const control = this.stepTypeControl || this.chunkTypeControl;
    if (control?.errors?.['required']) {
      return 'Step type is required';
    }
    return '';
  }

  // Legacy compatibility
  get isChunkTypeInvalid(): boolean {
    return this.isStepTypeInvalid;
  }

  get chunkTypeErrorMessage(): string {
    return this.stepTypeErrorMessage;
  }

  // Template helper methods to handle union types safely
  getAvailableTypes(): StepType[] {
    return this.stepTypes().length > 0 ? this.stepTypes() : this.chunkTypes();
  }

  getTypeId(type: StepType): number {
    return type.steptypeId;
  }
}
